/* eslint-disable react/no-multi-comp */
import React, { Fragment, useState } from 'react';
import { CSVLink } from 'react-csv';
import { usePopper } from 'react-popper';
import dynamic from 'next/dynamic';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import PropTypes from 'prop-types';

// Components
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Icon from '@/components/base/Icon';
import Select from '@/components/base/Select';
import Tooltip from '@/components/base/Tooltip';
import Instagram from '@/components/icons/Instagram';
import { PinterestIcon } from '@/components/icons/Networks/PinterestIcon';
import { YoutubeIcon } from '@/components/icons/Networks/YoutubeIcon';
import CustomDashboardWidgetActions from '@/components/pages/custom-dashboards/dashboard/CustomDashboardWidgetActions';
const CustomDashboardWidgetOptionSaver = dynamic(
    () => import('@/components/pages/custom-dashboards/dashboard/CustomDashboardWidgetOptionSaver')
);

// Hooks
import useUser from '@/hooks/swr/useUser';
import { useTableAndCSVData } from '@/components/composite/Charts/hooks/useTableAndCSVData';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { BASE } from '@/types/utils/sizesType';

// Lib
import { FACEBOOK, INSTAGRAM, LINKEDIN, PINTEREST, TWITTER, YOUTUBE } from '@/lib/socialProfile';

import { iconSize } from '@/components/base/Avatar/avatarConfig';

const ButtonClass =
    'w-10 h-10 rounded-full border border-icoGray-300 dark:border-0 dark:bg-icoDarkMode-deepBlack dark:text-icoDarkmode-wolf flex items-center justify-center text-icoGray-500';

const ListClassNameDashboardPopover =
    'rounded font-GMedium text-xs text-icoGray-500 transition-all hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoDarkMode-deepBlack';

export const VIEW_MODE = {
    TABLE: 'table',
    CHART: 'chart'
};

export const GROUP_VIEW_MODE = {
    CUMULATIVE: 'cumulative',
    COMPARATIVE: 'comparative'
};

const CSVExportLink = ({ data, columnNames }) => {
    const { t: tAnalytics } = useTranslation('analytics');
    const { t: tCommons } = useTranslation('commons');
    const { format } = useTableAndCSVData();
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const exportCSVDelimiter = user?.settings?.export_csv_delimiter;

    const [csvData, setCsvData] = useState([]);

    const formatCSVData = () => {
        let csvDataTmp = data?.graphData ?? [];
        let csvDataResult = [];

        // csv data default sorting
        if (data?.defaultTableSorting) {
            const key = data?.defaultTableSorting[0]?.id ?? '';
            const desc = data?.defaultTableSorting[0]?.desc ?? false;

            if (key) {
                csvDataTmp?.sort((a, b) => (desc ? b[key] - a[key] : a[key] - b[key]));
            }
        }

        csvDataTmp?.map((item) => {
            let obj = {};
            for (const [key, value] of Object.entries(item)) {
                let res = value;
                const arrayKey = columnNames[key] ?? '';

                if (arrayKey !== '') {
                    res = format({
                        isExport: true,
                        key: key,
                        value: value,
                        timeFormat: data.timeFormatter,
                        valueFormat: {},
                        tAnalytics: tAnalytics,
                        tCommons: tCommons,
                        formatterTranslation: data?.tableColumnFormatterTranslation
                    });

                    obj = { ...obj, [columnNames[key]]: res };
                }
            }

            csvDataResult.push(obj);
        });

        return csvDataResult;
    };

    return (
        <CSVLink
            className={classNames(
                'block rounded py-2 pl-1 duration-200 hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoBlue/30',
                {
                    'cursor-not-allowed opacity-50': data.length === 0
                }
            )}
            filename={data.id}
            aria-label={tCommons('export_as_csv')}
            data={csvData}
            asyncOnClick={true}
            onClick={(event, done) => {
                const result = formatCSVData();
                if (result.length === 0) return false;
                setCsvData(result);
                done();
            }}
            separator={exportCSVDelimiter ?? ','}>
            {tCommons('export_as_csv')}
        </CSVLink>
    );
};

const LeftHeaderChart = ({
    isDashboard,
    isExport,
    exportData,
    data,
    setRequestParams,
    socialProfileNetwork
}) => {
    if (isDashboard && data?.social_profiles?.length > 0) {
        return (
            <CustomDashboardWidgetActions.SelectSP
                socialProfiles={data.social_profiles}
                setRequestParams={setRequestParams}
                config={data}
            />
        );
    }

    if (isDashboard && data?.groups?.length > 0) {
        return (
            <CustomDashboardWidgetActions.SelectGroup
                groups={data.groups}
                setRequestParams={setRequestParams}
                config={data}
            />
        );
    }

    if (isExport && exportData.isMultiProfile && data?.social_profiles?.length > 0) {
        return (
            <Avatar
                forceLightMode={isExport}
                url={exportData?.social_profile?.external_api_data?.avatar}
                size={BASE}
                aria-label={exportData?.social_profile?.slug}
                fallback={exportData?.social_profile?.slug?.[0]}
                profileType={exportData?.social_profile?.social_network_type}
            />
        );
    }

    if (isExport && exportData.isMultiProfile && data?.groups?.length > 0) {
        return (
            <Avatar
                forceLightMode
                size={BASE}
                radius="rounded-md"
                fallback={exportData?.group?.name.substring(0, 2)}
                url={exportData?.group?.image}
                customColorClass={exportData?.group?.fallback_style}
            />
        );
    }

    if (socialProfileNetwork && socialProfileNetwork === INSTAGRAM) {
        return <Instagram data-testid="insta-icon" forceLightMode={isExport} />;
    }

    if (socialProfileNetwork && socialProfileNetwork === YOUTUBE) {
        return <YoutubeIcon forceLightMode={isExport} />;
    }

    if (socialProfileNetwork && socialProfileNetwork === PINTEREST) {
        return <PinterestIcon forceLightMode={isExport} />;
    }

    return (
        <Icon
            icon={socialProfileNetwork ?? ''}
            className={classNames(iconSize[BASE], {
                'text-facebook': socialProfileNetwork === FACEBOOK,
                'text-linkedin': socialProfileNetwork === LINKEDIN,
                'text-twitter': socialProfileNetwork === TWITTER,
                'dark:text-white': !isExport
            })}
        />
    );
};

const HeaderSubtitle = ({ isGroup, isDashboard, subtitle }) => {
    return (
        <span
            className={classNames(
                'block w-full text-left font-GMedium text-xxs italic text-icoGray-400',
                {
                    'ml-8': !isGroup && !isDashboard,
                    'ml-15': isDashboard
                }
            )}>
            {subtitle}
        </span>
    );
};

const ExportActions = ({
    isDashboard,
    data,
    handleSwitchTable,
    activeTable,
    generatePng,
    columnNames,
    onDelete,
    isComparativeViewActive,
    toggleComparativeView
}) => {
    const { t } = useTranslation('analytics');

    // Early return if export button should not be shown
    if (data.noExportButton && !isDashboard) {
        return null;
    }

    // Handle dashboard case
    if (isDashboard) {
        return (
            <CustomDashboardWidgetActions.Popover>
                {renderDashboardActions({
                    data,
                    isComparativeViewActive,
                    toggleComparativeView,
                    handleSwitchTable,
                    activeTable,
                    onDelete,
                    t
                })}
            </CustomDashboardWidgetActions.Popover>
        );
    }

    // Handle regular (non-dashboard) case
    return (
        <PopoverChart
            className={ButtonClass}
            buttonLabel={t('commons:export')}
            title={t('commons:export')}
            panel={
                <PopoverHeaderChart
                    handleSwitchTable={handleSwitchTable}
                    generatePng={generatePng}
                    columnNames={columnNames}
                    activeTable={activeTable}
                    noTableView={data.noTableView}
                    data={data}
                />
            }>
            <Icon icon="export" />
        </PopoverChart>
    );
};

const renderDashboardActions = ({
    data,
    isComparativeViewActive,
    toggleComparativeView,
    handleSwitchTable,
    activeTable,
    onDelete,
    t
}) => {
    return (
        <>
            {data.groups && data.hasComparativeView && (
                <li className={ListClassNameDashboardPopover}>
                    <CustomDashboardWidgetOptionSaver>
                        {(saveWidgetOption) => (
                            <button
                                className="w-full p-2 text-left"
                                onClick={() => {
                                    toggleComparativeView();
                                    saveWidgetOption(
                                        data,
                                        'groupViewMode',
                                        data.data?.groupViewMode === GROUP_VIEW_MODE.COMPARATIVE
                                            ? GROUP_VIEW_MODE.CUMULATIVE
                                            : GROUP_VIEW_MODE.COMPARATIVE,
                                        GROUP_VIEW_MODE.CUMULATIVE
                                    );
                                }}>
                                {isComparativeViewActive
                                    ? t('switch_cumulative_view')
                                    : t('switch_comparative_view')}
                            </button>
                        )}
                    </CustomDashboardWidgetOptionSaver>
                </li>
            )}

            {!data.noTableView && !data.noChartView && (
                <li className={ListClassNameDashboardPopover}>
                    <CustomDashboardWidgetOptionSaver>
                        {(saveWidgetOption) => (
                            <button
                                className="w-full p-2 text-left"
                                onClick={() => {
                                    handleSwitchTable();
                                    saveWidgetOption(
                                        data,
                                        'viewMode',
                                        data.data?.viewMode === VIEW_MODE.TABLE
                                            ? VIEW_MODE.CHART
                                            : VIEW_MODE.TABLE,
                                        VIEW_MODE.CHART
                                    );
                                }}>
                                {activeTable ? t('switch_chart_view') : t('switch_table_view')}
                            </button>
                        )}
                    </CustomDashboardWidgetOptionSaver>
                </li>
            )}

            <li className={ListClassNameDashboardPopover}>
                <button className="w-full p-2 text-left" onClick={onDelete}>
                    {t('remove_widget')}
                </button>
            </li>
        </>
    );
};

const ViewModeButton = ({ data, isDashboard, handleSwitchTable, activeTable }) => {
    const { t } = useTranslation('analytics');

    if (data.noTableView || data.noChartView || isDashboard) {
        return null;
    }

    return (
        <div>
            <button
                data-testid="switch-view"
                className={ButtonClass}
                aria-label={t('switch_view')}
                onClick={handleSwitchTable}
                title={activeTable ? t('switch_chart_view') : t('switch_table_view')}>
                <Icon icon={activeTable ? 'performance' : 'interface-list'} />
            </button>
        </div>
    );
};

const FilterComponent = ({
    data,
    isDashboard,
    activeFilter,
    setActiveFilter,
    setRequestParams,
    setState,
    children
}) => {
    const { t } = useTranslation('analytics');

    if (!data.filters || Object.keys(data.filters).length === 1) {
        return children;
    }

    return (
        <PopoverChart
            buttonLabel={t('filter')}
            panel={
                isDashboard ? (
                    <CustomDashboardWidgetOptionSaver>
                        {(saveWidgetOption) => (
                            <PopoverHeaderFilter
                                setActiveFilter={(e) => {
                                    setActiveFilter(e);
                                    setRequestParams({
                                        ...data,
                                        parameters: { ...data.parameters, type: e }
                                    });
                                    setState((prev) => {
                                        let type = 'social_profiles';
                                        if (data.groups?.length > 0) {
                                            type = 'groups';
                                        }
                                        return {
                                            ...prev,
                                            [type]: {
                                                ...prev[type],
                                                [data[type][0]]: {
                                                    ...prev[type][data[type][0]],
                                                    [data.id]: type === 'groups' ? [] : {}
                                                }
                                            }
                                        };
                                    });
                                    saveWidgetOption(data, 'type', e, Object.keys(data.filters)[0]);
                                }}
                                activeFilter={activeFilter}
                                filters={data.filters}
                            />
                        )}
                    </CustomDashboardWidgetOptionSaver>
                ) : (
                    <PopoverHeaderFilter
                        setActiveFilter={(e) => {
                            setActiveFilter(e);

                            setRequestParams((prev) => ({
                                ...prev,
                                parameters: {
                                    ...prev.parameters,
                                    [data.id]: {
                                        ...prev[data.id],
                                        type: e
                                    }
                                }
                            }));
                            setState((prev) =>
                                Array.isArray(prev)
                                    ? prev.map((prevItem) => ({
                                          ...prevItem,
                                          [data.id]: {}
                                      }))
                                    : {
                                          ...prev,
                                          [data.id]: {}
                                      }
                            );
                        }}
                        activeFilter={activeFilter}
                        filters={data.filters}
                    />
                )
            }>
            <div className="flex items-center text-left">
                {children}

                <Icon
                    icon="chevron-bottom"
                    className="pl-2 align-middle leading-none text-icoGray-400 hover:text-icoGray-500 dark:text-icoDarkMode-wolf"
                />
            </div>
        </PopoverChart>
    );
};

const TooltipComponent = ({ data, isExport }) => {
    const { t } = useTranslation('analytics');

    if (!data?.tooltip || isExport) {
        return null;
    }

    return (
        <span className="pl-3" id={`help_${data.id}`}>
            <Tooltip
                content={typeof data.tooltip === 'string' ? t(data.tooltip) : data.tooltip}
                placement="top"
                interactive={true}
                popperOptions={{
                    // Prevent tooltip to pop in the wrong place
                    strategy: 'absolute'
                }}>
                <Icon
                    aria-label={t('commons:infos')}
                    icon="info"
                    className={classNames(
                        'align-middle text-xs leading-none text-icoGray-400 hover:text-icoGray-500',
                        { 'dark:text-icoDarkMode-wolf': !isExport }
                    )}
                />
            </Tooltip>
        </span>
    );
};

const ComparativeViewComponent = ({
    data,
    isComparativeViewActive,
    toggleComparativeView,
    isDashboard
}) => {
    if (!(data.groups && data.hasComparativeView && !isDashboard)) {
        return null;
    }

    return (
        <SelectGroupViewMode
            isComparativeViewActive={isComparativeViewActive}
            toggleComparativeView={toggleComparativeView}
        />
    );
};

const HeaderChart = ({
    data,
    generatePng,
    handleSwitchTable,
    activeTable,
    columnNames,
    activeFilter,
    setActiveFilter,
    setRequestParams,
    setState,
    children,
    onDelete,
    isDashboard,
    isExport,
    exportData,
    isComparativeViewActive,
    toggleComparativeView
}) => {
    const { t } = useTranslation('analytics');
    const { activeSocialProfile } = useActiveSocialProfile();

    const socialProfileNetwork =
        exportData?.social_profile?.social_network_type ?? activeSocialProfile?.social_network_type;

    return (
        <div
            className={classNames('flex w-full items-center justify-between space-x-2', {
                'mb-5': !isExport,
                'mb-4': isExport
            })}>
            <div className="flex w-full flex-wrap items-center">
                {!(data?.groups?.length && !isDashboard && !isExport) && (
                    <div className="mr-3 flex items-center text-xl">
                        <LeftHeaderChart
                            isDashboard={isDashboard}
                            isExport={isExport}
                            exportData={exportData}
                            data={data}
                            setRequestParams={setRequestParams}
                            socialProfileNetwork={socialProfileNetwork}
                        />
                    </div>
                )}

                <FilterComponent
                    data={data}
                    isExport={isExport}
                    isDashboard={isDashboard}
                    activeFilter={activeFilter}
                    setActiveFilter={setActiveFilter}
                    setRequestParams={setRequestParams}
                    setState={setState}>
                    {children}
                </FilterComponent>

                <TooltipComponent data={data} isExport={isExport} />

                {(data.datePickerDoesNotApply || data.subtitle) && (
                    <HeaderSubtitle
                        isGroup={data?.groups?.length > 0}
                        isDashboard={isDashboard}
                        subtitle={t(
                            data.datePickerDoesNotApply
                                ? 'global_datepicker_does_not_apply'
                                : data.subtitle
                        )}
                    />
                )}
            </div>

            {!isExport && (
                <div className="flex items-center space-x-2" id={`chart_${data.id}`}>
                    <ComparativeViewComponent
                        data={data}
                        isComparativeViewActive={isComparativeViewActive}
                        toggleComparativeView={toggleComparativeView}
                        isDashboard={isDashboard}
                    />

                    <ViewModeButton
                        data={data}
                        isDashboard={isDashboard}
                        handleSwitchTable={handleSwitchTable}
                        activeTable={activeTable}
                    />

                    <ExportActions
                        isDashboard={isDashboard}
                        data={data}
                        handleSwitchTable={handleSwitchTable}
                        activeTable={activeTable}
                        generatePng={generatePng}
                        columnNames={columnNames}
                        onDelete={onDelete}
                        isComparativeViewActive={isComparativeViewActive}
                        toggleComparativeView={toggleComparativeView}
                    />
                </div>
            )}
        </div>
    );
};

HeaderChart.propType = {
    data: PropTypes.object,
    generatePng: PropTypes.func,
    handleSwitchTable: PropTypes.func,
    activeTable: PropTypes.bool,
    columnNames: PropTypes.object,
    children: PropTypes.node,
    activeFilter: PropTypes.string,
    setActiveFilter: PropTypes.func,
    setRequestParams: PropTypes.func,
    setState: PropTypes.func,
    exportData: PropTypes.object,
    onDelete: PropTypes.func,
    isDashboard: PropTypes.bool,
    isExport: PropTypes.bool,
    isComparativeViewActive: PropTypes.bool,
    toggleComparativeView: PropTypes.func
};

const PopoverChart = ({ panel, children, className, buttonLabel, ...restProps }) => {
    const [referenceElement, setReferenceElement] = useState();
    const [popperElement, setPopperElement] = useState();
    let { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 5]
                }
            }
        ]
    });

    return (
        <Popover className="relative" {...restProps}>
            {({ open, close }) => (
                <>
                    <PopoverButton
                        ref={setReferenceElement}
                        as="button"
                        aria-label={buttonLabel}
                        className={className}>
                        {React.Children.map(children, (child) => {
                            const newChild = React.cloneElement(child, {
                                open
                            });
                            return newChild;
                        })}
                    </PopoverButton>
                    <div
                        className="z-10 w-full"
                        ref={setPopperElement}
                        style={styles.popper}
                        {...attributes.popper}>
                        <Transition
                            show={open}
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1">
                            <PopoverPanel onClick={close}>
                                <div className="min-w-[110px] rounded-md border border-white bg-white/80 font-GSemiBold shadow-icoXL backdrop-blur-sm firefox:bg-gradient-to-tr firefox:from-icoGrayFirefox-300 firefox:to-icoWhiteFirefox dark:border-icoDarkMode-deepBlack dark:bg-icoDarkMode-anthracite/80">
                                    {panel}
                                </div>
                            </PopoverPanel>
                        </Transition>
                    </div>
                </>
            )}
        </Popover>
    );
};

PopoverChart.propTypes = {
    panel: PropTypes.node,
    children: PropTypes.node,
    className: PropTypes.string,
    buttonLabel: PropTypes.string
};

const PopoverHeaderChart = ({ activeTable, generatePng, noTableView, columnNames, data }) => {
    const { t } = useTranslation('analytics');

    return (
        <ul className="space-y-1 p-1 text-sm">
            {!activeTable ? (
                <li>
                    <button
                        className={classNames(
                            'w-full py-2 pl-1 text-left duration-200 hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoBlue/30',
                            {
                                'cursor-not-allowed opacity-50': data.length === 0
                            }
                        )}
                        aria-label={t('export_as_png')}
                        disabled={data.length === 0}
                        onClick={generatePng}>
                        {t('export_as_png')}
                    </button>
                </li>
            ) : null}
            {!noTableView ? (
                <li>
                    <CSVExportLink data={data} columnNames={columnNames} />
                </li>
            ) : null}
        </ul>
    );
};

PopoverHeaderChart.propType = {
    fileName: PropTypes.string,
    activeTable: PropTypes.bool,
    generatePng: PropTypes.func,
    csvData: PropTypes.array,
    noTableView: PropTypes.bool
};

const PopoverHeaderFilter = ({ filters, setActiveFilter, activeFilter }) => {
    const { t } = useTranslation('analytics');

    if (Object.keys(filters).length === 1) {
        return;
    }

    return (
        <ul className="px-1 py-2 text-sm">
            {Object.keys(filters).map((key) => (
                <li key={key}>
                    <button
                        className="w-full py-2 pl-1 text-left duration-200 hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoBlue/30"
                        aria-label={t(filters[key].menu)}
                        onClick={() => {
                            if (activeFilter !== key) {
                                setActiveFilter(key);
                            }
                        }}>
                        <span
                            className={classNames({
                                'text-icoBlue': key === activeFilter
                            })}>
                            {t(filters[key].menu)}
                        </span>
                    </button>
                </li>
            ))}
        </ul>
    );
};

PopoverHeaderFilter.propType = {
    filters: PropTypes.object,
    setActiveFilter: PropTypes.func,
    activeFilter: PropTypes.string
};

const SelectGroupViewMode = ({ isComparativeViewActive, toggleComparativeView }) => {
    const { t } = useTranslation('analytics');
    const groupViewModeOptions = [
        { value: 'cumulative', label: t('group_view_mode.cumulative') },
        { value: 'comparative', label: t('group_view_mode.comparative') }
    ];

    return (
        <Select
            noBorder
            className="mr-1"
            setSelected={(option) => {
                if (
                    (option.value === groupViewModeOptions[0].value && isComparativeViewActive) ||
                    (option.value !== groupViewModeOptions[0].value && !isComparativeViewActive)
                ) {
                    toggleComparativeView();
                }
            }}
            selected={groupViewModeOptions[isComparativeViewActive ? 1 : 0]}
            options={groupViewModeOptions}
            button={<SelectGroupViewModeButton />}
        />
    );
};

SelectGroupViewMode.propType = {
    isComparativeViewActive: PropTypes.bool,
    toggleComparativeView: PropTypes.func
};

const SelectGroupViewModeButton = ({ selected }) => {
    return (
        <Select.Button selected={selected} baseStyle="grayNoBorder" sizeStyle="sm">
            <span className="flex items-center space-x-2 pr-2 text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                <Icon icon="visible" className="text-base" />
                <span>{selected?.label}</span>
            </span>
        </Select.Button>
    );
};

SelectGroupViewModeButton.propType = {
    selected: PropTypes.func
};

export default HeaderChart;
